import axios from "axios";
import config from "services/config";

const BASE = `${config.url}/accounting`;

function adminHeaders() {
  return { Authorization: `Basic ${process.env.REACT_APP_BASIC_AUTH}` };
}

// ─── Plan de cuentas ──────────────────────────────────────────────
export const AccountingService = {
  async listAccounts(memberId) {
    const { data } = await axios.get(`${BASE}/accounts/${memberId}`, { headers: adminHeaders() });
    return data;
  },
  async createAccount(memberId, payload) {
    const { data } = await axios.post(`${BASE}/accounts/${memberId}`, payload, { headers: adminHeaders() });
    return data;
  },
  async updateAccount(memberId, code, payload) {
    const { data } = await axios.patch(`${BASE}/accounts/${memberId}/${encodeURIComponent(code)}`, payload, { headers: adminHeaders() });
    return data;
  },
  async deleteAccount(memberId, code) {
    const { data } = await axios.delete(`${BASE}/accounts/${memberId}/${encodeURIComponent(code)}`, { headers: adminHeaders() });
    return data;
  },
  async seedAccounts(memberId) {
    const { data } = await axios.post(`${BASE}/accounts/${memberId}/seed`, {}, { headers: adminHeaders() });
    return data;
  },

  // ─── Asientos ────────────────────────────────────────────────
  async listEntries(memberId, { month, year } = {}) {
    const { data } = await axios.get(`${BASE}/journal/${memberId}`, {
      params: { month, year },
      headers: adminHeaders(),
    });
    return data;
  },
  async createEntry(memberId, payload) {
    const { data } = await axios.post(`${BASE}/journal/${memberId}`, payload, { headers: adminHeaders() });
    return data;
  },
  async deleteEntry(memberId, id) {
    const { data } = await axios.delete(`${BASE}/journal/${memberId}/${id}`, { headers: adminHeaders() });
    return data;
  },
  async generateFromRcv(memberId, { month, year }) {
    const { data } = await axios.post(`${BASE}/journal/${memberId}/generate-from-rcv`, { month, year }, { headers: adminHeaders() });
    return data;
  },

  // ─── Reportes ────────────────────────────────────────────────
  async getLedger(memberId, { accountCode, from, to }) {
    const { data } = await axios.get(`${BASE}/ledger/${memberId}`, {
      params: { accountCode, from, to },
      headers: adminHeaders(),
    });
    return data;
  },
  async getF29(memberId, { month, year }) {
    const { data } = await axios.get(`${BASE}/f29/${memberId}`, {
      params: { month, year },
      headers: adminHeaders(),
    });
    return data;
  },

  // ─── Sprint 1: Reportes engine ──────────────────────────────────
  async getTrialBalance(memberId, { month, year }) {
    const { data } = await axios.get(`${BASE}/reports/trial-balance/${memberId}`, { params: { month, year }, headers: adminHeaders() });
    return data;
  },
  async getIncomeStatement(memberId, { month, year }) {
    const { data } = await axios.get(`${BASE}/reports/income-statement/${memberId}`, { params: { month, year }, headers: adminHeaders() });
    return data;
  },
  async getBalanceSheet(memberId, { month, year }) {
    const { data } = await axios.get(`${BASE}/reports/balance-sheet/${memberId}`, { params: { month, year }, headers: adminHeaders() });
    return data;
  },
  async getBalance8Col(memberId, { month, year }) {
    const { data } = await axios.get(`${BASE}/reports/balance-8-col/${memberId}`, { params: { month, year }, headers: adminHeaders() });
    return data;
  },
  async getF29Full(memberId, { month, year, regime } = {}) {
    const { data } = await axios.get(`${BASE}/f29/${memberId}/full`, { params: { month, year, regime }, headers: adminHeaders() });
    return data;
  },

  // PDF/Excel descargas — devuelven la URL absoluta autenticada para usar como href.
  // (en MUI usaremos fetch + blob + window.open por la basic-auth)
  reportPdfUrl(memberId, kind, { month, year }) {
    return `${BASE}/reports/${kind}/${memberId}.pdf?month=${month}&year=${year}`;
  },
  reportXlsxUrl(memberId, kind, { month, year }) {
    return `${BASE}/reports/${kind}/${memberId}.xlsx?month=${month}&year=${year}`;
  },
  async downloadReport(memberId, kind, ext, { month, year }) {
    const url = ext === "pdf" ? this.reportPdfUrl(memberId, kind, { month, year }) : this.reportXlsxUrl(memberId, kind, { month, year });
    const response = await axios.get(url, { headers: adminHeaders(), responseType: "blob" });
    const blob = new Blob([response.data], {
      type: ext === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const dlUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = dlUrl;
    a.download = `${kind}-${year}-${String(month).padStart(2, "0")}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(dlUrl), 1000);
  },

  // ─── Sprint 2: Obligaciones tributarias ─────────────────────────
  async listTaxObligations(memberId, { from, to, status } = {}) {
    const { data } = await axios.get(`${BASE}/tax-obligations/${memberId}`, { params: { from, to, status }, headers: adminHeaders() });
    return data;
  },
  async recalculateTaxObligations(memberId, payload = {}) {
    const { data } = await axios.post(`${BASE}/tax-obligations/${memberId}/recalculate`, payload, { headers: adminHeaders() });
    return data;
  },
  async declareTaxObligation(memberId, id, payload) {
    const { data } = await axios.post(`${BASE}/tax-obligations/${memberId}/${id}/declare`, payload, { headers: adminHeaders() });
    return data;
  },
  async markTaxObligationPaid(memberId, id) {
    const { data } = await axios.post(`${BASE}/tax-obligations/${memberId}/${id}/paid`, {}, { headers: adminHeaders() });
    return data;
  },
};
